import { useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';
import Skeleton from 'react-loading-skeleton';

import SectionTitle from '../SectionTitle/SectionTitle';
import LinkNumbers from '../LinkNumbers/LinkNumbers';
import TextBlock from '../TextBlock/TextBlock';
import Button from '../Button/Button';
import Tabs from '../Tabs/Tabs';
import TabPanel from '../Tabs/TabPanel';
import Tab from '../Tabs/Tab';
import TabContent from '../Tabs/TabContent';
import TabContentItem from '../Tabs/TabContentItem';
import CompatibleDevicesModal from '../CompatibleDevicesModal/CompatibleDevicesModal';
import VideoModal from '../VideoModal/VideoModal';

import styles from './StepsBanner.module.scss';

const StepsBannerWithTabs = ({ data, className, devices }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const initialState = {
    url: data.tabs[0]?.list[0]?.screen?.path,
    alt: data.tabs[0]?.list[0]?.alt?.path,
  };
  const [imageData, setImageData] = useState(initialState);
  const [activeClass, setActiveClass] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [activeModal, setActiveModal] = useState(0);
  const [activeVideoModal, setActiveVideoModal] = useState(0);

  const handleChangeTab = (index) => {
    setImageData(initialState);
    setActiveTab(index);
    setActiveClass(0);
  };

  const popup = () => {
    setActiveVideoModal('teyhlo_cDGo');
  };

  const setActiveScreen = (index) => {
    setActiveClass(index);
    setImageData({
      url: data.tabs[activeTab]?.list[index]?.screen?.path,
      alt: data.tabs[activeTab]?.list[index]?.alt?.path,
    });
  };

  const renderSteps = (item, index) => {
    const currentActiveElement =
      activeClass === index ? styles['step-item--active'] : '';

    return (
      <div
        className={classNames(styles['step-item'], currentActiveElement)}
        key={index}
        onClick={() => setActiveScreen(index)}
        itemScope
        itemType='https://schema.org/Property'
      >
        <div className={styles['step-item__count']}>
          {`${index + 1} ${item.title}`}
        </div>
        <div className={styles['step-item__text']}>{item.desc}</div>
      </div>
    );
  };

  const renderTab = (item, index) => {
    return (
      <Tab key={index} onClick={() => handleChangeTab(index)}>
        {item.name}
      </Tab>
    );
  };

  const renderContent = (item, index) => {
    return (
      <TabContentItem key={index}>
        {item?.list?.map(renderSteps)}
      </TabContentItem>
    );
  };

  return (
    <section
      className={classNames(styles['step-banner'], className)}
      itemScope
      itemType='https://schema.org/Offer'
    >
      <div className={styles['step-banner__wrapper']}>
        <div className={styles['step-banner__content']}>
          <div className={styles['step-banner__head']}>
            {data?.title && (
              <SectionTitle
                titleModify={classNames(
                  styles['step-banner__title'],
                  'title--large',
                )}
              >
                {data.title}
              </SectionTitle>
            )}
            {data?.numbers && (
              <div className={styles['step-banner__link']}>
                Dial
                <LinkNumbers numbers={data.numbers} isTel />
                to activate
              </div>
            )}
            {data?.desc && (
              <TextBlock
                className={classNames(
                  styles['step-banner__text'],
                  'text--medium',
                )}
              >
                {data.desc}
              </TextBlock>
            )}
          </div>
          <Tabs active={activeTab}>
            <TabPanel className={styles.tabs}>
              {data?.tabs?.map(renderTab)}
            </TabPanel>
            <TabContent>{data?.tabs?.map(renderContent)}</TabContent>
          </Tabs>
          <div className={styles['step-banner__buttons']}>
            <Button onClick={popup}>Watch the Video</Button>
            <Button variant='border' onClick={() => setActiveModal(true)}>
              Compatible Devices
            </Button>
          </div>
        </div>
        <div className={styles['step-banner__phone-wrapper']}>
          <div className={styles['step-banner__phone']}>
            {!isImageLoaded && <Skeleton width='100%' height='100%' />}
            <Image
              className={styles['step-banner__phone-frame']}
              src='/assets/services/phone-frame.png'
              alt='screen'
              loading='lazy'
              layout='fill'
              onLoadingComplete={() => setIsImageLoaded(true)}
              style={{
                visibility: isImageLoaded ? 'visible' : 'hidden',
              }}
            />
            <Image
              className={styles['step-banner__phone-screen']}
              src={imageData.url}
              alt={imageData.alt}
              loading='lazy'
              layout='fill'
              onLoadingComplete={() => setIsImageLoaded(true)}
              style={{
                visibility: isImageLoaded ? 'visible' : 'hidden',
              }}
            />
          </div>
        </div>
      </div>
      <CompatibleDevicesModal
        active={activeModal}
        setActive={setActiveModal}
        devices={devices}
      />
      <VideoModal
        videoId={activeVideoModal}
        onClose={() => setActiveVideoModal(null)}
      />
    </section>
  );
};

export default StepsBannerWithTabs;
